import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { notificationApi } from "../api/notification.api";
import { fromNow } from "@/lib/utils/format";
import { Button } from "@/components/ui/button";
import { Dropdown } from "@/components/ui/dropdown";

// Strips the handful of inline tags (<strong>, mostly) the backend's email
// templates use -- appropriate for an HTML email body, not for this plain
// in-app feed. Rendered as plain text rather than dangerouslySetInnerHTML:
// some of these messages fold in HR-typed free text (a rejection reason, a
// document comment), and this is a shared feed other staff also see, so
// there's no reason to render arbitrary HTML here at all.
const stripHtml = (s = "") => s.replace(/<[^>]*>/g, "");

// Polling, not a websocket -- this app has no real-time push
// infrastructure, so a 25s refetch is the honest stand-in for "real-time"
// rather than overclaiming instant delivery.
const POLL_INTERVAL_MS = 25000;

export function NotificationBell() {
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: notificationApi.unreadCount,
    refetchInterval: POLL_INTERVAL_MS,
  });
  const { data } = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => notificationApi.list({ limit: 8 }),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({ mutationFn: (id) => notificationApi.markRead(id), onSuccess: invalidate });
  const markAllRead = useMutation({ mutationFn: () => notificationApi.markAllRead(), onSuccess: invalidate });

  const count = unread?.count || 0;
  const items = data?.items || [];

  return (
    <Dropdown
      className="w-80 p-0"
      trigger={
        <Button variant="ghost" size="icon" aria-label={count > 0 ? `${count} unread notifications` : "Notifications"} className="relative">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span
              style={{ fontFamily: '"Poppins", "Outfit", sans-serif' }}
              className="absolute right-1.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-white"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      }
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <p className="text-[16px] font-bold">Notifications</p>
        {count > 0 && (
          <button type="button" onClick={() => markAllRead.mutate()} className="text-[14px] font-semibold text-primary hover:underline">
            Mark all read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-[15px] font-medium text-muted-foreground">You're all caught up.</p>
        ) : (
          items.map((n) => (
            <Link
              key={n._id}
              to={n.link || "/notifications"}
              onClick={() => !n.read && markRead.mutate(n._id)}
              className={`block border-b border-border px-3 py-2.5 last:border-0 hover:bg-accent ${!n.read ? "bg-primary-muted/50" : ""}`}
            >
              <p className="text-[15px] font-semibold leading-snug">{n.title}</p>
              {n.body && <p className="mt-0.5 line-clamp-2 text-[14px] font-medium text-muted-foreground">{stripHtml(n.body)}</p>}
              <p className="mt-1 text-[15px] font-medium text-muted-foreground">{fromNow(n.createdAt)}</p>
            </Link>
          ))
        )}
      </div>

      <Link to="/notifications" className="block border-t border-border px-3 py-2.5 text-center text-[14px] font-semibold text-primary hover:bg-accent">
        View all notifications
      </Link>
    </Dropdown>
  );
}
